
// Pure, screen-space (2D pixel) predicates for box-selection (see
// BoxSelection.js for the THREE.js/DOM integration - projecting world
// vertices to screen coordinates, looping dm.visuals, deciding scope, and
// applying the result all live there, Level 2/3, verified live). This file
// only ever answers "is this 2D point/segment inside or crossing this
// axis-aligned rectangle" - genuinely pure, Level 1 (no-browser) unit
// testable, deliberately separate from anything THREE/DOM-entangled, same
// split this codebase already uses throughout geometry/.
//
// Not a reuse of geometry/polygonValidity.js's segmentsIntersect() or
// geometry/lineIntersection2D.js's classifyIntersection3D() - those solve a
// different problem (two arbitrary world-space segments, with their own
// epsilon conventions tuned to survey-scale coordinates) than "an
// axis-aligned pixel rectangle vs. a segment," which is simpler and needs
// pixel-scale (not metre-scale) tolerance. A fresh, minimal, purpose-built
// primitive is the smaller, clearer change.

const EPS = 1e-9;

// Builds the rectangle + CAD-style directional mode from a drag's start/end
// screen points (canvas-relative pixels, same convention as
// DigitizationModule's own _toCanvasMouse()/projectToScreen()). Direction is
// judged purely from the rectangle's own x-extent, independent of which
// mouse button/modifier triggered the drag: dragging left-to-right (window)
// is a precise/contained selection; right-to-left (crossing) is a loose
// contains-or-crosses selection - the same convention professional CAD/GIS
// tools (AutoCAD, QGIS) already use.
export function rectFromDrag(start, end){
	const direction = start.x <= end.x ? "window" : "crossing";
	return {
		minX: Math.min(start.x, end.x),
		maxX: Math.max(start.x, end.x),
		minY: Math.min(start.y, end.y),
		maxY: Math.max(start.y, end.y),
		direction,
	};
}

export function pointInRect(point, rect){
	return point.x >= rect.minX && point.x <= rect.maxX && point.y >= rect.minY && point.y <= rect.maxY;
}

// Standard bounded segment-segment intersection (orientation/straddle test,
// including the collinear-touching special case) - same textbook approach
// as geometry/polygonValidity.js's own segmentsIntersect(), reimplemented
// here in plain {x,y} form at pixel-scale EPS rather than importing that
// function (which is tuned to polygon-area-scale epsilon and 2-element
// array/tuple inputs, not this file's own plain-object/pixel-scale needs).
function orientation(ax, ay, bx, by, cx, cy){
	const val = (by - ay) * (cx - bx) - (bx - ax) * (cy - by);
	if(Math.abs(val) < EPS) return 0;
	return val > 0 ? 1 : 2;
}

function onSegment(ax, ay, bx, by, px, py){
	return px <= Math.max(ax, bx) + EPS && px >= Math.min(ax, bx) - EPS
		&& py <= Math.max(ay, by) + EPS && py >= Math.min(ay, by) - EPS;
}

function segmentsIntersect2D(a1, a2, b1, b2){
	const o1 = orientation(a1.x, a1.y, a2.x, a2.y, b1.x, b1.y);
	const o2 = orientation(a1.x, a1.y, a2.x, a2.y, b2.x, b2.y);
	const o3 = orientation(b1.x, b1.y, b2.x, b2.y, a1.x, a1.y);
	const o4 = orientation(b1.x, b1.y, b2.x, b2.y, a2.x, a2.y);

	if(o1 !== o2 && o3 !== o4) return true;

	if(o1 === 0 && onSegment(a1.x, a1.y, a2.x, a2.y, b1.x, b1.y)) return true;
	if(o2 === 0 && onSegment(a1.x, a1.y, a2.x, a2.y, b2.x, b2.y)) return true;
	if(o3 === 0 && onSegment(b1.x, b1.y, b2.x, b2.y, a1.x, a1.y)) return true;
	if(o4 === 0 && onSegment(b1.x, b1.y, b2.x, b2.y, a2.x, a2.y)) return true;

	return false;
}

// True if segment a->b is either fully/partially inside `rect`, OR crosses
// one of its 4 edges - i.e. "crossing-mode" inclusion (used for whole-
// feature selection, task section 6: "krydsning... hele features... enten
// ligger inde i, eller krydser rectangle"). NOT used for window-mode
// (vertex-only, see BoxSelection.js's own scope decision - a segment being
// "fully inside" for window purposes is just both its endpoints individually
// passing pointInRect()).
export function segmentIntersectsRect(a, b, rect){
	if(pointInRect(a, rect) || pointInRect(b, rect)) return true;

	const topLeft = {x: rect.minX, y: rect.minY};
	const topRight = {x: rect.maxX, y: rect.minY};
	const bottomRight = {x: rect.maxX, y: rect.maxY};
	const bottomLeft = {x: rect.minX, y: rect.maxY};

	return segmentsIntersect2D(a, b, topLeft, topRight)
		|| segmentsIntersect2D(a, b, topRight, bottomRight)
		|| segmentsIntersect2D(a, b, bottomRight, bottomLeft)
		|| segmentsIntersect2D(a, b, bottomLeft, topLeft);
}

// Selection-model audit finding: a window-mode box-selection landing on a
// single feature's vertices is only USEFULLY distinct from the existing
// generic vertex-multiSelection when the selected indices are a contiguous
// ascending run - that's the one case with an established, pre-existing
// meaning (a "range"/"sammenhængende del", same concept the 2-click "Vælg
// sammenhængende del" flow already produces) or, if the run is literally
// every vertex, whole-feature selection. Any other shape (multiple visuals,
// or a non-contiguous subset within one visual) has no such existing
// operation to route to, so it falls back to the generic vertex-scope
// multiSelection unchanged. Pure/Level-1: takes only a sorted index array
// and the feature's total vertex count, no visual/THREE.js involved - see
// BoxSelection.js for the caller that extracts these from actual entries.
export function classifyVertexIndexRun(sortedIndices, vertexCount){
	for(let i = 1; i < sortedIndices.length; i++){
		if(sortedIndices[i] !== sortedIndices[i - 1] + 1) return null;
	}

	const start = sortedIndices[0];
	const end = sortedIndices[sortedIndices.length - 1];
	if(start === 0 && end === vertexCount - 1) return {type: "full"};
	return {type: "range", start, end};
}
